import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, MapPin, Calendar, Clock, Image as ImageIcon,
  CheckCircle, AlertTriangle, PenLine, ArrowLeft, ClipboardCheck, Download, X,
} from 'lucide-react';

const imageBase = 'https://app.sowashusa.com/uploads/';
const signatureFolder = 'signatures/';

// Same design tokens as the commercial report (pages/CI/JobHistory.js)
const T = {
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  pink:    '#F472B6',
  green:   '#34D399',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes ajd-spin { to { transform: rotate(360deg); } }
  @keyframes ajd-fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .ajd-img { transition: opacity .2s, transform .2s; }
  .ajd-img:hover { opacity: .85; transform: scale(1.02); }

  .ajd-label {
    display: block; font-size: 9px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--nd-tx30); margin-bottom: 5px;
  }
  .ajd-field-cell {
    background: var(--nd-w03); border: 1px solid var(--nd-w08);
    border-radius: 10px; padding: 12px 14px;
  }
  .ajd-check-cell {
    display: flex; align-items: center; justify-content: space-between;
    background: var(--nd-w03); border: 1px solid var(--nd-w08);
    border-radius: 10px; padding: 10px 14px;
  }
  .ajd-check-cell.active-check {
    background: rgba(52,211,153,0.08); border-color: rgba(52,211,153,0.25);
  }
  .ajd-timeline-dot {
    width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }
  .ajd-btn-primary {
    padding: 9px 16px; border: none; border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong);
    display: flex; align-items: center; justify-content: center; gap: 7px;
    box-shadow: 0 4px 14px rgba(59,130,246,0.28); transition: opacity .15s, transform .15s;
  }
  .ajd-btn-primary:hover { opacity: .9; transform: translateY(-1px); }
  .ajd-btn-primary:disabled { opacity: .5; cursor: not-allowed; transform: none; }
  .ajd-btn-ghost {
    padding: 9px 16px; border: 1px solid var(--nd-w12); border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600;
    background: var(--nd-w04); color: var(--nd-tx60);
    display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: background .15s;
  }
  .ajd-btn-ghost:hover { background: var(--nd-w08); color: var(--nd-text); }
`;

// Varchar booleans arrive as 'yes'/'no' (or true/false/null) — see CLAUDE.md
const normBool = (v) => v === true || (typeof v === 'string' && v.toLowerCase() === 'yes');
const isNo = (v) => v === false || (typeof v === 'string' && v.toLowerCase() === 'no');

const SecHead = ({ icon: Icon, label, color, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
    <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}18`, border: `1px solid ${color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={15} color={color} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{label}</span>
    {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
  </div>
);

const FieldCell = ({ label, value, span }) => (
  <div className="ajd-field-cell" style={span ? { gridColumn: '1 / -1' } : {}}>
    <div className="ajd-label">{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{value || 'N/A'}</div>
  </div>
);

const Section = ({ children, style }) => (
  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 18px', marginBottom: 16, ...style }}>
    {children}
  </div>
);

const pill = (color, bg, border) => ({
  padding: '3px 9px', background: bg, color, border: `1px solid ${border}`, borderRadius: 6,
  fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: T.mono,
});

const AdminJobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!id) return;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/job-metrics/job-details/${id}`)
      .then(r => setJob(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [id]);

  const generatePDF = async () => {
    setDownloading(true);
    const el = document.getElementById('job-details-content');
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');
      // html2canvas (1.4.1) cannot parse CSS variables — it must receive a resolved color string,
      // so read the active theme's --nd-bg off :root (works in both light and dark themes).
      const bg = getComputedStyle(document.documentElement).getPropertyValue('--nd-bg').trim() || '#ffffff';
      el.style.overflow = 'visible';
      const canvas = await html2canvas(el, { scale:2, useCORS:true, logging:false, backgroundColor:bg });
      el.style.overflow = '';
      const pdf = new jsPDF('p','mm','a4');
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      const r = Math.min(pw/canvas.width, ph/canvas.height);
      pdf.addImage(canvas.toDataURL('image/png'),'PNG',(pw-canvas.width*r)/2,10,canvas.width*r,canvas.height*r);
      pdf.save(`Job_Report_${job.job_id}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      if (el) el.style.overflow = '';
      console.error('PDF generation failed:', e);
      alert('Failed to generate PDF.');
    }
    finally { setDownloading(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : null;
  const fmtDateTime = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : null;
  const dur = (m) => { if(!m) return 'N/A'; const h=Math.floor(m/60),mn=m%60; return h>0?`${h}h ${mn}m`:`${mn}m`; };

  if (loading) return (
    <div style={{ padding:'14px 20px', fontFamily:T.font, display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}>
      <style>{STYLES}</style>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:40, height:40, border:`3px solid ${T.border}`, borderTop:`3px solid ${T.blue}`, borderRadius:'50%', animation:'ajd-spin .8s linear infinite', margin:'0 auto 14px' }}/>
        <div style={{ color:T.muted, fontSize:13 }}>Loading details…</div>
      </div>
    </div>
  );

  if (!job) return (
    <div style={{ padding:'14px 20px', fontFamily:T.font, textAlign:'center', paddingTop:80 }}>
      <style>{STYLES}</style>
      <FileText size={48} color="var(--nd-w15)" style={{ marginBottom:16 }}/>
      <p style={{ color:'var(--nd-tx40)', fontSize:16, fontWeight:700 }}>Job not found</p>
      <button className="ajd-btn-primary" onClick={()=>navigate(-1)} style={{ margin:'16px auto 0' }}>Go Back</button>
    </div>
  );

  const fsr = job.fsr_fields;
  const isCompleted = !!job.completed_at;
  const sigSrc = job.customer_signature
    ? (job.customer_signature.startsWith('signature_')
        ? `${imageBase}${signatureFolder}${job.customer_signature}`
        : `${imageBase}${job.customer_signature}`)
    : null;

  const timeline = [
    { label:'Scheduled',    val:fmtDate(job.scheduled_date),    color:T.blue,   Icon:Calendar,    always:true },
    { label:'Work Started', val:fmtDateTime(job.started_at),    color:T.green,  Icon:Clock },
    { label:'Completed',    val:fmtDateTime(job.completed_at),  color:T.pink,   Icon:CheckCircle },
  ];

  return (
    <div style={{ padding:'14px 20px', fontFamily:T.font }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ maxWidth:880, margin:'0 auto', background:T.surface, border:`1px solid ${T.bord2}`, borderRadius:20, overflow:'hidden', marginBottom:16, animation:'ajd-fadeUp .3s ease both' }}>
        <div style={{ padding:'18px 22px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap', background:'var(--nd-w02)' }}>
          <div style={{ flex:1, minWidth:220 }}>
            <div style={{ fontSize:9, color:T.muted, letterSpacing:'0.18em', marginBottom:4 }}>RESIDENTIAL CLEANING · JOB #{job.job_id}</div>
            <div style={{ fontSize:18, fontWeight:800, color:'var(--nd-text-strong)', letterSpacing:'-0.02em', marginBottom:6 }}>
              {job.customer?.name || `Job #${job.job_id}`}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              {isCompleted
                ? <span style={pill(T.green,'rgba(52,211,153,0.10)','rgba(52,211,153,0.26)')}>✓ completed</span>
                : <span style={pill(T.amber,'rgba(251,191,36,0.10)','rgba(251,191,36,0.26)')}>⏳ in progress</span>}
              <span style={pill(
                (job.payment_status||'').toLowerCase()==='paid' ? T.green : T.red,
                (job.payment_status||'').toLowerCase()==='paid' ? 'rgba(52,211,153,0.10)' : 'rgba(248,113,113,0.10)',
                (job.payment_status||'').toLowerCase()==='paid' ? 'rgba(52,211,153,0.26)' : 'rgba(248,113,113,0.26)'
              )}>
                {job.payment_status || 'Unknown'}
              </span>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, flexShrink:0 }}>
            <button className="ajd-btn-ghost" onClick={()=>navigate(-1)}><ArrowLeft size={14}/>Back</button>
            <button className="ajd-btn-primary" onClick={generatePDF} disabled={downloading}>
              {downloading
                ? <span style={{ width:13, height:13, border:'2px solid var(--nd-tx25)', borderTop:'2px solid white', borderRadius:'50%', animation:'ajd-spin .7s linear infinite', display:'inline-block' }}/>
                : <Download size={14}/>}
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
        </div>
        <div style={{ height:2, background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      <div id="job-details-content" style={{ maxWidth:880, margin:'0 auto' }}>
        {/* Customer / site information */}
        <Section>
          <SecHead icon={MapPin} label="Customer Information" color={T.blue}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <FieldCell label="Client"      value={job.customer?.name}/>
            <FieldCell label="Job Type"    value="Residential Cleaning"/>
            <FieldCell label="Phone"       value={job.customer?.phone}/>
            <FieldCell label="City"        value={job.customer?.city}/>
            {job.system_size_kw > 0 && <FieldCell label="System Size" value={`${job.system_size_kw} kW`}/>}
            <FieldCell label="Job Number"  value={`#${job.job_id}`}/>
          </div>
        </Section>

        {/* Timeline */}
        <Section>
          <SecHead icon={Clock} label="Job Timeline" color={T.blue}/>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {timeline.map(({ label, val, color, Icon, always }) => {
              if (!val && !always) return null;
              return (
                <div key={label} style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                  <div className="ajd-timeline-dot" style={{ background:`${color}12`, border:`1.5px solid ${color}30` }}>
                    <Icon size={15} color={color}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:700, color, marginBottom:3 }}>{label}</div>
                    <div style={{ fontSize:11, color:T.muted, fontFamily:T.mono }}>{val || 'Pending'}</div>
                  </div>
                </div>
              );
            })}
            <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
              <div className="ajd-timeline-dot" style={{ background:'rgba(167,139,250,0.12)', border:'1.5px solid rgba(167,139,250,0.30)' }}>
                <Clock size={15} color={T.purple}/>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.purple, marginBottom:3 }}>Total Duration</div>
                <div style={{ fontSize:13, fontWeight:700, color:T.purple, fontFamily:T.mono }}>{dur(job.duration_minutes)}</div>
              </div>
            </div>
          </div>
        </Section>

        {/* Photo gallery */}
        <Section>
          <SecHead icon={ImageIcon} label="Photo Gallery" color={T.purple}/>
          {[
            { label:'📷 Before Photos', key:'before_images' },
            { label:'📷 After Photos',  key:'after_images'  },
          ].map(({ label, key }) => (
            <div key={key} style={{ marginBottom:14 }}>
              <div className="ajd-label" style={{ marginBottom:8 }}>{label}{job[key]?.length > 0 ? ` (${job[key].length})` : ''}</div>
              {job[key]?.length > 0 ? (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))', gap:8 }}>
                  {job[key].map((f,i) => (
                    <img
                      key={i}
                      className="ajd-img"
                      src={`${imageBase}${f}`}
                      alt=""
                      onClick={()=>setSelectedImage(`${imageBase}${f}`)}
                      style={{ width:'100%', aspectRatio:'1', objectFit:'cover', borderRadius:10, border:`1px solid ${T.border}`, cursor:'pointer' }}
                    />
                  ))}
                </div>
              ) : (
                <div className="ajd-field-cell" style={{ textAlign:'center', color:T.muted, fontSize:12, padding:'22px 14px' }}>
                  <ImageIcon size={24} color="var(--nd-w10)" style={{ marginBottom:6 }}/>
                  <div>No images available</div>
                </div>
              )}
            </div>
          ))}
        </Section>

        {/* Field Service Report */}
        {fsr && (
          <Section>
            <SecHead icon={ClipboardCheck} label="Field Service Report" color={T.blue}/>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              <FieldCell label="Cable Condition" value={fsr.cable_condition}/>
              <FieldCell label="Cable Quantity"  value={fsr.cable_quantity}/>
              <FieldCell label="Panel Damage"    value={fsr.panel_damage}/>
              <FieldCell label="Panel Brand"     value={fsr.panel_brand}/>
              <FieldCell label="Inverter Alarm"  value={fsr.inverter_alarm}/>
              <FieldCell label="Alarm Code"      value={fsr.alarm_code}/>
              <FieldCell label="Potential Shading" value={fsr.potential_shading}/>
              <div className="ajd-field-cell" style={{ display:'flex', alignItems:'center' }}>
                <div>
                  <div className="ajd-label">Overall</div>
                  {(fsr.cable_condition === 'Good' && isNo(fsr.inverter_alarm) && (isNo(fsr.panel_damage) || fsr.panel_damage === 'None'))
                    ? <div style={{ display:'flex', alignItems:'center', gap:7 }}><CheckCircle size={16} color={T.green}/><span style={{ color:T.green, fontWeight:700, fontSize:13 }}>No issues</span></div>
                    : <div style={{ display:'flex', alignItems:'center', gap:7 }}><AlertTriangle size={16} color={T.amber}/><span style={{ color:T.amber, fontWeight:700, fontSize:13 }}>Needs attention</span></div>}
                </div>
              </div>
            </div>

            {normBool(fsr.potential_shading) && (
              <div className="ajd-field-cell" style={{ marginBottom:12 }}>
                <div className="ajd-label">Shading Details</div>
                <div style={{ fontSize:13, color:T.muted, lineHeight:1.6 }}>{fsr.shading_details || 'No details provided'}</div>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:9, marginTop:4 }}>
              {[
                { label:'Rusting',       key:'rusting',        icon:'🔧' },
                { label:'Bird Dropping', key:'bird_dropping',  icon:'🦅' },
                { label:'Moss & Debris', key:'mos_and_debris', icon:'🍃' },
                { label:'Earthing',      key:'earthing',       icon:'⚡' },
              ].map(({ label, key, icon }) => {
                const active = normBool(fsr[key]);
                return (
                  <div key={key} className={`ajd-check-cell ${active ? 'active-check' : ''}`}>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <span style={{ fontSize:16 }}>{icon}</span>
                      <span style={{ fontSize:12, fontWeight:600, color:T.muted }}>{label}</span>
                    </div>
                    <span style={{ fontSize:14, fontWeight:700, color: active ? T.green : 'var(--nd-w20)' }}>{active ? '✓' : '✗'}</span>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Signature */}
        <Section>
          <SecHead icon={PenLine} label="Customer Signature" color={T.pink}/>
          <div style={{ textAlign:'center' }}>
            {sigSrc
              ? <img src={sigSrc} alt="Signature" crossOrigin="anonymous" style={{ maxWidth:240, borderRadius:10, border:`1px solid ${T.bord2}`, padding:14, background:'var(--nd-w02)' }}/>
              : <div className="ajd-field-cell" style={{ color:T.muted, fontSize:12, padding:'22px 14px' }}>
                  <PenLine size={24} color="var(--nd-w10)" style={{ marginBottom:6 }}/>
                  <div>No signature available</div>
                </div>}
          </div>
        </Section>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div
          onClick={()=>setSelectedImage(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.80)', backdropFilter:'blur(5px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:20 }}
        >
          <div style={{ position:'relative', maxWidth:'90vw', maxHeight:'90vh' }} onClick={e=>e.stopPropagation()}>
            <button
              onClick={()=>setSelectedImage(null)}
              style={{ position:'absolute', top:-14, right:-14, width:32, height:32, borderRadius:8, background:'var(--nd-w10)', border:`1px solid ${T.bord2}`, color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
            ><X size={16}/></button>
            <img src={selectedImage} alt="" style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:12 }}/>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobDetails;
